import React, { useEffect, useState, useRef } from 'react';
import * as api from './api';
import { hasRealmRole, hasClientRole } from './api';
import { handleRedirect, login, logout, refreshIfNeeded, isAuthenticated, getUserInfo } from './auth';
import { DEMO_MODE, ON_CALLBACK, ENVIRONMENT_DEV } from './config';
import Sidebar from './components/Sidebar';
import AppHeader from './components/AppHeader';
import { NotificationsProvider } from './components/NotificationsBell';
import Dashboard from './pages/Dashboard';
import History from './pages/History';
import RatingsPage from './pages/RatingsPage';
import TemplatesAdmin from './pages/admin/Templates';
import ModelsAdmin from './pages/admin/Models';
import FormAdmin from './pages/admin/Form';
import FormView from './pages/admin/FormView';
import CreditorsAdmin from './pages/admin/Creditors';
import ScenariosView from './pages/admin/ScenariosView';
import ScenarioForm from './pages/admin/ScenariosForm';
import ConvaiHistory from './pages/ConvaiHistory';
import ConvaiHistoryDetails from './pages/ConvaiHistoryDetails';
import UserScenarios from './pages/UserScenarios';
import UserMoods from './pages/UserMoods';
import LLMTestCases from './pages/LLMTestCases';
import TestCasesResults from './pages/TestCasesResults';
import RealCallAdmin from "./pages/admin/RealCall";



export default function App() {
  const [page, setPage] = useState('dashboard');
  const [convaiConversationId, setConvaiConversationId] = useState('');
  const [convaiHistoryCallType, setConvaiHistoryCallType] = useState('web_call');
  const [drawer, setDrawer] = useState(false);
  const [historyCount, setHistoryCount] = useState(null);

  const [authReady, setAuthReady] = useState(false);
  const [profile, setProfile] = useState(null);
  const [formClientId, setFormClientId] = useState(null);
  const [scenarioId, setScenarioId] = useState(null);

  useEffect(() => {
    if (ENVIRONMENT_DEV) return;
    if (
      page === 'user_scenarios' ||
      page === 'user_moods' ||
      page === 'llm_test_cases' ||
      page === 'test_cases_results'
    ) {
      setPage('dashboard');
    }
  }, [page]);

  useEffect(() => {
    if (DEMO_MODE) return;
    if (!authReady) return;

    const ensureFresh = async () => {
      try {
        await refreshIfNeeded(true);
        if (!isAuthenticated()) {
          login();
        } else {
          setProfile(getUserInfo());
        }
      } catch {
        if (!isAuthenticated()) {
          login();
        } else {
          setProfile(getUserInfo());
        }
      }
    };

    const onFocus = () => ensureFresh();
    const onVisible = () => { if (!document.hidden) ensureFresh(); };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [authReady]);

  useEffect(() => {
    if (DEMO_MODE) return;
    if (!authReady) return;
    (async () => {
      if (!isAuthenticated()) {
        let refreshed = false;
        try {
          refreshed = await refreshIfNeeded(true);
        } catch {
          refreshed = false;
        }
        if (!refreshed && !isAuthenticated()) {
          login();
        } else {
          setProfile(getUserInfo());
        }
      }
    })();
  }, [authReady, page]);

  const didHandle = useRef(false);

  useEffect(() => {
    (async () => {
      if (DEMO_MODE) {
        setProfile(null);
        setAuthReady(true);
        return;
      }

      if (ON_CALLBACK) {
        if (didHandle.current) return;
        didHandle.current = true;

        await handleRedirect();
        setProfile(getUserInfo());
        setAuthReady(true);
        return;
      }

      try { await refreshIfNeeded(); } catch { }
      if (!isAuthenticated()) {
        login();
        return;
      }
      setProfile(getUserInfo());
      setAuthReady(true);
    })();
  }, []);

  useEffect(() => {
    if (DEMO_MODE) return;
    const i = setInterval(async () => {
      try {
        const changed = await refreshIfNeeded();
        if (changed) setProfile(getUserInfo());
      } catch {
      }
    }, 30_000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    if (!authReady || (!isAuthenticated() && !DEMO_MODE)) return;
    (async () => {
      try {
        const data = await api.historyPaged({ page: 1, perPage: 1 });
        const total = (data?.counts?.rated ?? 0) + (data?.counts?.unrated ?? 0);
        setHistoryCount(total);
      } catch (e) { console.error(e); }
    })();
  }, [authReady]);

  useEffect(() => {
    if (page !== 'history' || !authReady || (!isAuthenticated() && !DEMO_MODE)) return;
    (async () => {
      try {
        const data = await api.historyPaged({ page: 1, perPage: 1 });
        const total = (data?.counts?.rated ?? 0) + (data?.counts?.unrated ?? 0);
        setHistoryCount(total);
      } catch (e) { console.error(e); }
    })();
  }, [page, authReady]);

  const [canManageModels, setCanManageModels] = useState(false);

  useEffect(() => {
    if (!authReady) return;
    const allowed =
      hasRealmRole('SuperVoice') ||
      hasClientRole(process.env.KEYCLOAK_CLIENT_ID || 'your-client-id', 'SuperVoice');
    setCanManageModels(!!allowed);
  }, [authReady, profile]);

  const isReady = authReady && (isAuthenticated() || DEMO_MODE);
  const showNotifications = !DEMO_MODE && isAuthenticated();

  return (
    <div className="min-h-screen overflow-x-hidden">
      {isReady && (
        <Sidebar
          page={page}
          setPage={(p) => { setPage(p); setDrawer(false); }}
          historyCount={historyCount}
          isAuthed={isAuthenticated() || DEMO_MODE}
          drawer={drawer}
          setDrawer={setDrawer}
          showUserScenarios={ENVIRONMENT_DEV}
          showUserMoods={ENVIRONMENT_DEV}
          showLlmTestCases={ENVIRONMENT_DEV}
          showTestCasesResults={ENVIRONMENT_DEV}
        />
      )}

      <NotificationsProvider enabled={showNotifications}>
        <main className="p-6 md:p-8 md:pl-[var(--sbpad)] transition-[padding]">
          {isReady ? (
            <>
              <AppHeader
                drawer={drawer}
                setDrawer={setDrawer}
                showNotifications={showNotifications}
                isAuthed={isAuthenticated() || DEMO_MODE}
                setPage={setPage}
                canManageModels={canManageModels}
                profile={profile}
                onLogout={() => logout(true)}
              />

              <div className="space-y-6">
              {page === 'dashboard' && (
                <Dashboard onAfterUpload={async () => {
                  setHistoryCount(c => (c ?? 0) + 1);
                  try {
                    const data = await api.historyPaged({ page: 1, perPage: 1 });
                    const total = (data?.counts?.rated ?? 0) + (data?.counts?.unrated ?? 0);
                    setHistoryCount(total);
                  } catch { }
                }} />
              )}
              {page === "real_call" && (<RealCallAdmin onBack={() => setPage("dashboard")} />)}
              {page === 'history' && <History setPage={setPage} />}
              {page === 'ratings' && <RatingsPage />}
              {page === 'templates' && <TemplatesAdmin onBack={() => setPage('dashboard')} />}
              {page === 'creditors' && <CreditorsAdmin onBack={() => setPage('dashboard')} />}
              {page === 'models' && canManageModels && <ModelsAdmin onBack={() => setPage('dashboard')} />}
              {page === 'form' && (
                <FormAdmin
                  clientId={formClientId}
                  onBack={() => setPage('form_view')}
                  onSaved={() => setPage('form_view')}
                />
              )}

              {page === 'form_view' && (
                <FormView
                  onBack={() => setPage('dashboard')}
                  onAdd={() => { setFormClientId(null); setPage('form'); }}
                  onEdit={(id) => { setFormClientId(id); setPage('form'); }}
                />
              )}

              {page === 'scenarios' && (
                <ScenariosView
                  onBack={() => setPage('dashboard')}
                  onAdd={() => { setScenarioId(null); setPage('scenario_form'); }}
                  onEdit={(id) => { setScenarioId(id); setPage('scenario_form'); }}
                />
              )}

              {page === 'scenario_form' && (
                <ScenarioForm
                  scenarioId={scenarioId}
                  onBack={() => setPage('scenarios')}
                  onSaved={() => setPage('scenarios')}
                />
              )}
              {page === 'convai_history' && (
                <ConvaiHistory
                  callType={convaiHistoryCallType}
                  onCallTypeChange={setConvaiHistoryCallType}
                  onOpenConversation={(id) => {
                    setConvaiConversationId(id);
                    setPage('convai_history_details');
                  }}
                />
              )}

              {page === 'convai_history_details' && (
                <ConvaiHistoryDetails
                  conversationId={convaiConversationId}
                  onBack={() => setPage('convai_history')}
                />
              )}

              {ENVIRONMENT_DEV && page === 'user_scenarios' && (
                <UserScenarios />
              )}

              {ENVIRONMENT_DEV && page === 'user_moods' && (
                <UserMoods />
              )}

              {ENVIRONMENT_DEV && page === 'llm_test_cases' && (
                <LLMTestCases />
              )}

              {ENVIRONMENT_DEV && page === 'test_cases_results' && (
                <TestCasesResults />
              )}

              {page === 'models' && !canManageModels && (
                <div className="card rounded-2xl p-6 text-slate-300">Нямате достъп.</div>
              )}
              </div>
            </>
          ) : DEMO_MODE ? (
            <div className="card rounded-2xl p-6">
              <div className="text-slate-300">Демо режим.</div>
            </div>
          ) : (
            <div className="card rounded-2xl p-6">
              <div className="text-slate-300 mb-3">Пренасочване към екран за вход…</div>
            </div>
          )}
        </main>
      </NotificationsProvider>
    </div>
  );
}
